import { useEffect, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { PageShell } from "@/components/marketing/PageShell";
import { api } from "@/lib/api";

const usd = (n) => n.toLocaleString("en-US", { style: "currency", currency: "USD" });

const Cancelled = () => (
  <div className="text-center" data-testid="payment-cancelled">
    <XCircle className="mx-auto h-10 w-10 text-oath-muted" />
    <h2 className="mt-4 font-display text-2xl font-bold text-oath-ink">Payment cancelled</h2>
    <p className="mt-2 text-sm text-oath-secondary">No charge was made. You can pick a plan again whenever you're ready.</p>
  </div>
);

const Status = ({ sessionId }) => {
  const [state, setState] = useState({ phase: "checking" });
  useEffect(() => {
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") return setState({ phase: "paid", data });
        if (data.payment_status === "expired") return setState({ phase: "failed" });
      } catch {
        return setState({ phase: "failed" });
      }
      tries += 1;
      if (tries >= 10) return setState({ phase: "pending" });
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  if (state.phase === "checking") return <div className="text-center" data-testid="payment-checking"><Loader2 className="mx-auto h-8 w-8 animate-spin text-oath-muted" /><p className="mt-3 text-sm text-oath-secondary">Confirming your payment…</p></div>;
  if (state.phase === "paid") return (
    <div className="text-center" data-testid="payment-success">
      <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" />
      <h2 className="mt-4 font-display text-2xl font-bold text-oath-ink">Payment received</h2>
      <p className="mt-2 text-sm text-oath-secondary" data-testid="payment-summary">{state.data.description} · {usd(state.data.amount)}</p>
      <p className="mt-2 text-sm text-oath-secondary">Our team will set up your organization and email your console invitation.</p>
    </div>
  );
  if (state.phase === "pending") return <div className="text-center" data-testid="payment-pending"><h2 className="font-display text-2xl font-bold text-oath-ink">Still processing</h2><p className="mt-2 text-sm text-oath-secondary">We haven't received confirmation yet. Please check your email shortly.</p></div>;
  return <div className="text-center" data-testid="payment-failed"><h2 className="font-display text-2xl font-bold text-oath-ink">We couldn't confirm this payment</h2><p className="mt-2 text-sm text-oath-secondary">If you were charged, contact us and we'll sort it out.</p></div>;
};

export default function PaymentResult() {
  const [params] = useSearchParams();
  const { pathname } = useLocation();
  const sessionId = params.get("session_id");
  return (
    <PageShell eyebrow="— PAYMENT —">
      <div className="mx-auto max-w-xl rounded-3xl border border-gray-200 bg-white p-10">
        {pathname.endsWith("cancel") || !sessionId ? <Cancelled /> : <Status sessionId={sessionId} />}
        <div className="mt-8 flex justify-center gap-4 text-sm">
          <Link to="/pricing" className="text-oath-accent underline" data-testid="payment-back-pricing">Back to pricing</Link>
          <Link to="/contact" className="text-oath-accent underline">Contact us</Link>
        </div>
      </div>
    </PageShell>
  );
}
